import { Link, useParams, Navigate } from "react-router-dom";
import {
  ArrowLeft,
  ArrowUpRight,
  MapPin,
  Calendar,
  Layers,
  Briefcase,
  Clock,
  FileText,
  Sofa,
  Zap,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useLang } from "@/context/LanguageContext";
import { PROJECT_ASSETS } from "@/lib/projects";

const CIVIL_IMG =
  "https://images.pexels.com/photos/5511065/pexels-photo-5511065.jpeg?auto=compress&cs=tinysrgb&w=1600";
const MEP_IMG =
  "https://images.unsplash.com/photo-1581092160562-40aa08e78837?auto=format&fit=crop&w=1600&q=80";

const ICON_MAP = {
  file: (props) => <FileText {...props} strokeWidth={1.5} />,
  arrow: (props) => <ArrowUpRight {...props} />,
  sofa: (props) => <Sofa {...props} strokeWidth={1.5} />,
  zap: (props) => <Zap {...props} strokeWidth={1.5} />,
  clock: (props) => <Clock {...props} />,
};

const ProjectDetail = () => {
  const { slug } = useParams();
  const { t } = useLang();

  const project = t.projects.list.find((p) => p.slug === slug);
  const detail = t.projectDetail.details?.[slug];
  const asset = PROJECT_ASSETS[slug];

  if (!project || !detail || !asset) {
    return <Navigate to="/" replace />;
  }

  const pd = t.projectDetail;

  return (
    <div
      data-testid="project-detail-page"
      data-project-slug={slug}
      className="bg-[#121212] text-white min-h-screen"
    >
      <Navbar />

      {/* Back link */}
      <div className="pt-28 md:pt-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
          <Link
            to="/#projects"
            data-testid="project-back-link"
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft size={14} /> {pd.back}
          </Link>
        </div>
      </div>

      {/* Header */}
      <section className="pt-10 md:pt-16 pb-10">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 flex-wrap mb-6">
                <span className="bg-[#FF3B30] text-white font-mono text-[10px] tracking-[0.25em] px-3 py-1.5">
                  {project.num}
                </span>
                <span className="border border-white/20 text-white/70 font-mono text-[10px] tracking-[0.25em] px-3 py-1.5">
                  {project.category}
                </span>
                <span className="font-mono text-[10px] tracking-[0.25em] text-white/40">
                  {project.location}
                </span>
              </div>
              <h1
                className="font-display font-extrabold text-white leading-[0.9] tracking-tight"
                style={{ fontSize: "clamp(2.25rem, 6.5vw, 6rem)" }}
                data-testid="project-title"
              >
                {project.title}
                <span className="text-[#FF3B30]">.</span>
              </h1>
              {detail.subtitle && (
                <p
                  className="mt-6 text-white/60 text-base md:text-lg italic max-w-3xl leading-relaxed"
                  data-testid="project-subtitle"
                >
                  {detail.subtitle}
                </p>
              )}
            </div>
            <div className="lg:col-span-4 grid grid-cols-2 gap-6">
              <MetaCard icon={<MapPin size={14} />} label={pd.metaLocation} value={project.location} />
              <MetaCard icon={<Calendar size={14} />} label={pd.metaYear} value={project.year} />
              <MetaCard icon={<Layers size={14} />} label={pd.metaScope} value={detail.metaScopeValue} />
              <MetaCard icon={<Briefcase size={14} />} label={pd.metaClient} value={pd.metaClientValue} />
            </div>
          </div>
        </div>
      </section>

      {/* Main image */}
      <section className="pb-16 md:pb-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="relative overflow-hidden bg-[#1c1c1e] aspect-[16/10]">
            <img
              src={asset.image}
              alt={project.title}
              className="w-full h-full object-cover"
              data-testid="project-hero-image"
            />
          </div>
        </div>
      </section>

      {/* Overview + Actions */}
      <section className="pb-24 md:pb-32">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-6">
              <div className="flex items-center gap-3 mb-6">
                <span className="h-px w-8 bg-[#FF3B30]" />
                <p className="font-mono text-[11px] tracking-[0.3em] text-white/60">
                  {pd.overview}
                </p>
              </div>
              <h2
                className="font-display font-extrabold text-white leading-[0.95] tracking-tight mb-8"
                style={{ fontSize: "clamp(1.75rem, 3.5vw, 3rem)" }}
              >
                {project.title}
              </h2>
              <p className="text-white/70 text-base md:text-lg leading-relaxed">
                {detail.overviewText}
              </p>
            </div>

            {/* Action buttons */}
            <div className="lg:col-span-6 lg:pl-8 space-y-4">
              {detail.buttons.map((btn, i) => {
                const Icon = ICON_MAP[btn.icon] || ICON_MAP.arrow;
                const href = asset.buttons?.[btn.key];
                return (
                  <ActionButton
                    key={btn.key}
                    title={btn.label}
                    status={btn.status}
                    icon={<Icon size={18} />}
                    href={btn.type === "link" ? href : null}
                    primary={btn.primary}
                    disabled={btn.type === "disabled"}
                    testId={`project-btn-${btn.key}`}
                  />
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="pb-24 md:pb-32 bg-[#0a0a0a] border-y border-white/10 py-16 md:py-24">
        <div className="max-w-[1600px] mx-auto px-6 md:px-10 lg:px-16">
          <div className="flex items-center gap-3 mb-10">
            <span className="h-px w-8 bg-[#FF3B30]" />
            <p className="font-mono text-[11px] tracking-[0.3em] text-white/60">
              {pd.gallery}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            <div className="md:col-span-8 aspect-[16/10] overflow-hidden bg-[#1c1c1e]">
              <img
                src={asset.image}
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
            <div className="md:col-span-4 grid grid-cols-1 gap-6">
              <div className="aspect-square overflow-hidden bg-[#1c1c1e]">
                <img
                  src={CIVIL_IMG}
                  alt=""
                  className="w-full h-full object-cover grayscale"
                />
              </div>
              <div className="aspect-square overflow-hidden bg-[#1c1c1e]">
                <img
                  src={MEP_IMG}
                  alt=""
                  className="w-full h-full object-cover grayscale"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const MetaCard = ({ icon, label, value }) => (
  <div className="border-t border-white/10 pt-4">
    <div className="flex items-center gap-2 text-white/40 mb-2">
      {icon}
      <p className="font-mono text-[10px] tracking-[0.25em]">{label}</p>
    </div>
    <p className="font-display font-extrabold text-white text-base md:text-lg leading-tight">
      {value}
    </p>
  </div>
);

const ActionButton = ({ title, status, icon, href, primary, disabled, testId }) => {
  const base =
    "group w-full flex items-center justify-between gap-6 px-6 py-6 transition-all duration-300 border";

  if (disabled) {
    return (
      <div
        data-testid={testId}
        className={`${base} border-white/10 bg-[#1c1c1e] text-white/40 cursor-not-allowed`}
      >
        <div className="flex items-center gap-4">
          <span className="text-white/30">{icon}</span>
          <div className="text-left">
            <p className="font-display font-extrabold text-lg md:text-xl text-white/50 leading-tight">
              {title}
            </p>
            <p className="font-mono text-[10px] tracking-[0.25em] text-white/30 mt-1">
              {status}
            </p>
          </div>
        </div>
        <span className="font-mono text-[10px] tracking-[0.25em] text-white/30">
          —
        </span>
      </div>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      data-testid={testId}
      className={`${base} ${
        primary
          ? "bg-[#FF3B30] border-[#FF3B30] hover:bg-[#D63026] text-white"
          : "border-white/20 hover:border-white text-white"
      }`}
    >
      <div className="flex items-center gap-4">
        <span>{icon}</span>
        <div className="text-left">
          <p className="font-display font-extrabold text-lg md:text-xl leading-tight">
            {title}
          </p>
          <p className="font-mono text-[10px] tracking-[0.25em] opacity-70 mt-1">
            {status}
          </p>
        </div>
      </div>
      <ArrowUpRight
        size={22}
        className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform"
      />
    </a>
  );
};

export default ProjectDetail;
